/**
 * Mods, and what it takes to earn them.
 *
 * A mod is a switch on the title screen that changes the rules: faster
 * tank, walls that bounce everything, paper with no colour on it. Each one is
 * locked behind something you do in the game rather than something you buy,
 * and the record of what you have done lives in one small stats object that
 * the game pokes as things happen. The garage (`tanks.ts`) reads the same
 * record for its own locks. Nothing here knows physics or the DOM;
 * it knows what is unlocked, what is switched on, and where that is kept.
 */

export type ModId = "turbo" | "rubber" | "longfuse" | "glass" | "ink" | "mirror";

export type Stats = {
  /** Ricochets off scenery, ever. */
  bounces: number;
  /** Held two power-ups at once. */
  stacked: boolean;
  /** Cleared any level under par. */
  parBeaten: boolean;
  /** Reached five stars of heat. */
  stars5: boolean;
  /** The most levels cleared in one run. */
  cleared: number;
  /** Runs finished. */
  wins: number;
  /** Landed a ×5 combo. */
  combo5: boolean;
  /** Fired all three power-ups at once. */
  sink: boolean;
};

/** How far along an unlock is: `value` of `target`, so a bar can be drawn. */
export type Progressed = { value: number; target: number };

export type Mod = {
  id: ModId;
  name: string;
  blurb: string;
  /** What earns it, as shown while it is still locked. */
  unlock: string;
  earned: (s: Stats) => boolean;
  progress: (s: Stats) => Progressed;
};

/** A yes-or-no unlock, as a bar that is empty or full. */
export const once = (done: boolean): Progressed => ({ value: done ? 1 : 0, target: 1 });

export const MODS: Mod[] = [
  {
    id: "turbo",
    name: "Turbo",
    blurb: "+33% drive, faster boost.",
    unlock: "Beat any par",
    earned: (s) => s.parBeaten,
    progress: (s) => once(s.parBeaten),
  },
  {
    id: "rubber",
    name: "Rubber Walls",
    blurb: "Every shell bounces twice.",
    unlock: "40 ricochets",
    earned: (s) => s.bounces >= 40,
    progress: (s) => ({ value: Math.min(40, s.bounces), target: 40 }),
  },
  {
    id: "longfuse",
    name: "Long Fuse",
    blurb: "Power-ups last 30s.",
    unlock: "Hold 2 power-ups at once",
    earned: (s) => s.stacked,
    progress: (s) => once(s.stacked),
  },
  {
    id: "ink",
    name: "Ink Only",
    blurb: "No colour, ink only.",
    unlock: "Reach 5 heat stars",
    earned: (s) => s.stars5,
    progress: (s) => once(s.stars5),
  },
  {
    id: "mirror",
    name: "Mirror",
    blurb: "Every map flipped.",
    unlock: "Clear 5 levels in a run",
    earned: (s) => s.cleared >= 5,
    progress: (s) => ({ value: Math.min(5, s.cleared), target: 5 }),
  },
  {
    id: "glass",
    name: "Glass Cannon",
    blurb: "1 armour, double damage.",
    unlock: "Clear all 10 levels",
    earned: (s) => s.wins >= 1,
    progress: (s) => once(s.wins >= 1),
  },
];

const FRESH: Stats = {
  bounces: 0,
  stacked: false,
  parBeaten: false,
  stars5: false,
  cleared: 0,
  wins: 0,
  combo5: false,
  sink: false,
};

/** Where things are kept. localStorage in the app; a Map in a test. */
export type Store = { get(key: string): string | null; set(key: string, value: string): void };

const KEY_STATS = "paper-tank.stats";
const KEY_MODS = "paper-tank.mods";
const KEY_SEEN = "paper-tank.seen";

export const localStore: Store = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // No store; it lasts as long as the visit.
    }
  },
};

/** Anything else that unlocks off the same record, the garage for one. */
export type Earnable = { id: string; earned: (s: Stats) => boolean };

export class Progress {
  readonly stats: Stats;
  private enabled: Set<ModId>;
  /** Things already announced, so a toast is shown once. */
  private seen: Set<string>;

  constructor(private store: Store = localStore) {
    this.stats = { ...FRESH, ...parse<Partial<Stats>>(store.get(KEY_STATS), {}) };
    this.enabled = new Set(parse<ModId[]>(store.get(KEY_MODS), []).filter((id) => this.unlocked(id)));
    this.seen = new Set(parse<string[]>(store.get(KEY_SEEN), []));
  }

  /**
   * Which of these are earned and have not been announced yet. Calling it
   * marks them announced, so the first call after a fresh start returns
   * whatever was always earned, so pass those through `seed` to keep quiet.
   */
  newTanks<T extends Earnable>(specs: T[], seed = false): T[] {
    const fresh = specs.filter((t) => t.earned(this.stats) && !this.seen.has(t.id));
    if (fresh.length) {
      for (const t of fresh) this.seen.add(t.id);
      this.store.set(KEY_SEEN, JSON.stringify([...this.seen]));
    }
    return seed ? [] : fresh;
  }

  unlocked(id: ModId): boolean {
    const mod = MODS.find((m) => m.id === id);
    return mod ? mod.earned(this.stats) : false;
  }

  on(id: ModId): boolean {
    return this.enabled.has(id);
  }

  /** Flip a mod. Returns whether it is on afterwards; a locked mod stays off. */
  toggle(id: ModId): boolean {
    if (!this.unlocked(id)) return false;
    if (this.enabled.has(id)) this.enabled.delete(id);
    else this.enabled.add(id);
    this.store.set(KEY_MODS, JSON.stringify([...this.enabled]));
    return this.enabled.has(id);
  }

  /**
   * Change the record and find out what that earned. Cheap to call often:
   * nothing is written unless something actually changed.
   */
  record(change: (s: Stats) => void): Mod[] {
    const before = JSON.stringify(this.stats);
    const had = MODS.filter((m) => m.earned(this.stats));
    change(this.stats);
    const after = JSON.stringify(this.stats);
    if (after === before) return [];
    this.store.set(KEY_STATS, after);
    return MODS.filter((m) => m.earned(this.stats) && !had.includes(m));
  }
}

function parse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

// ---- plain settings --------------------------------------------------------

export function readSetting(name: string, fallback: string, store: Store = localStore): string {
  return store.get(`paper-tank.${name}`) ?? fallback;
}

export function writeSetting(name: string, value: string, store: Store = localStore): void {
  store.set(`paper-tank.${name}`, value);
}
